/**
 * Projects API — thin typed wrappers around /api/kanban/projects/*.
 *
 * Phase 5 surface: workspace-scoped CRUD on chaos.projects + project
 * member management. The hook layer (TanStack Query) caches with the
 * keys exported below.
 */
import type {
  AddProjectMemberInput,
  CreateProjectInput,
  Project,
  ProjectMemberRole,
  ProjectMemberWithUser,
  UpdateProjectInput,
} from '@chaos/types/project';

import { apiJson } from './client';

interface ProjectsResponse {
  projects: Project[];
}
interface ProjectResponse {
  project: Project;
}
interface MembersResponse {
  members: ProjectMemberWithUser[];
}
interface MemberResponse {
  member: ProjectMemberWithUser;
}

export const projectsApi = {
  list(): Promise<Project[]> {
    return apiJson<ProjectsResponse>('/kanban/projects').then((r) => r.projects);
  },
  get(projectId: string): Promise<Project> {
    return apiJson<ProjectResponse>(`/kanban/projects/${projectId}`).then((r) => r.project);
  },
  create(input: CreateProjectInput): Promise<Project> {
    return apiJson<ProjectResponse>('/kanban/projects', {
      method: 'POST',
      json: input,
    }).then((r) => r.project);
  },
  update(projectId: string, input: UpdateProjectInput): Promise<Project> {
    return apiJson<ProjectResponse>(`/kanban/projects/${projectId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.project);
  },
  archive(projectId: string): Promise<Project> {
    return apiJson<ProjectResponse>(`/kanban/projects/${projectId}`, {
      method: 'DELETE',
    }).then((r) => r.project);
  },
  listMembers(projectId: string): Promise<ProjectMemberWithUser[]> {
    return apiJson<MembersResponse>(`/kanban/projects/${projectId}/members`).then((r) => r.members);
  },
  addMember(projectId: string, input: AddProjectMemberInput): Promise<ProjectMemberWithUser> {
    return apiJson<MemberResponse>(`/kanban/projects/${projectId}/members`, {
      method: 'POST',
      json: input,
    }).then((r) => r.member);
  },
  updateMember(
    projectId: string,
    userId: string,
    role: ProjectMemberRole,
  ): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/kanban/projects/${projectId}/members/${userId}`, {
      method: 'PATCH',
      json: { role },
    });
  },
  removeMember(projectId: string, userId: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/kanban/projects/${projectId}/members/${userId}`, {
      method: 'DELETE',
    });
  },
};

export const projectKeys = {
  all: ['projects'] as const,
  list: () => [...projectKeys.all, 'list'] as const,
  detail: (projectId: string) => [...projectKeys.all, 'detail', projectId] as const,
  members: (projectId: string) => [...projectKeys.all, 'members', projectId] as const,
};
